import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import portfolioData from "./data.json"; // Importing the local JSON file

const FundPerformance = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [monthlyReturns, setMonthlyReturns] = useState([]);
  const [bestPerformer, setBestPerformer] = useState(null); // Track the best performer
  const [worstPerformer, setWorstPerformer] = useState(null); // Track the worst performer
  const [securitiesToAdd, setSecuritiesToAdd] = useState([]); // Empty list to hold securities to be added


  useEffect(() => {
    // Get current date
    const currentDate = new Date();

    const securitiesList = [
      "HCLTECH",
      // "ANANTRAJ",
      // "EMBDL",
      // "OILCOUNTUB",
      "ASTEC",
      "NIRAJ",
      // "SBCL",
      // "SDBL",
      "JSL",
      // "IKS",
    ];

    // Update the securities list state
    setSecuritiesToAdd(securitiesList);

    // Filter portfolio for Purchase Date > May 2023
    const filteredPortfolio = portfolioData.filter((stock) => {
      const purchaseDate = new Date(stock["Purchase Date"]);
      return purchaseDate > new Date("2023-01-01");
    });

    // Remove rows where "Buy Price" is NaN
    const validPortfolio = filteredPortfolio.filter((stock) => {
      return !isNaN(parseFloat(stock["Buy Price"])); // Ensure Buy Price is a valid number
    });

    // Sort the filtered portfolio by "Purchase Date" in ascending order (earliest first)
    const sortedPortfolio = validPortfolio.sort((a, b) =>
      new Date(a["Purchase Date"]) - new Date(b["Purchase Date"])
    );
    setPortfolio(sortedPortfolio);

    // Calculate monthly returns based on Sell Date
    calculateMonthlyReturns(sortedPortfolio);

    // Calculate the best and worst performers
    findBestAndWorstPerformers(sortedPortfolio);
  }, []);

  const calculateMonthlyReturns = (data) => {
    const monthlyAggregatedReturns = {};
    const currentDate = new Date(); // Get the current date for checking

    data.forEach((stock) => {
      const sellDate = new Date(stock["Sell Date"]);
      const monthKey = `${sellDate.getFullYear()}-${sellDate.getMonth() + 1}`; // Year-Month format
      const returnValue = stock["Return"]; // Assume "Return" is directly available here as a percentage

      // Check if the current date is before the sell date
      if (currentDate < sellDate) {
        // Find the latest available month for aggregation
        const latestMonthKey = Object.keys(monthlyAggregatedReturns).pop();
        if (latestMonthKey) {
          monthlyAggregatedReturns[latestMonthKey] += returnValue; // Add return to the latest available month
        }
      } else {
        // Aggregate returns for the same sell month if the stock is sold
        if (!monthlyAggregatedReturns[monthKey]) {
          monthlyAggregatedReturns[monthKey] = 0;
        }
        monthlyAggregatedReturns[monthKey] += returnValue;
      }
    });

    // Prepare data for the chart, and divide monthly returns by 120
    const aggregatedData = Object.keys(monthlyAggregatedReturns).map((monthKey) => {
      return {
        name: monthKey, // Year-Month format
        return: monthlyAggregatedReturns[monthKey] / 120, // Scale by 120
      };
    });

    // Sort the months by the key (Year-Month format)
    aggregatedData.sort((a, b) => {
      const [aYear, aMonth] = a.name.split("-").map(Number);
      const [bYear, bMonth] = b.name.split("-").map(Number);
      return aYear !== bYear ? aYear - bYear : aMonth - bMonth;
    });

    setMonthlyReturns(aggregatedData);
  };

  const findBestAndWorstPerformers = (data) => {
    // Find the best performer (highest return) and worst performer (lowest return)
    let best = null;
    let worst = null;

    data.forEach((stock) => {
      const returnValue = stock["Return"];

      if (!best || returnValue > best["Return"]) {
        best = stock;
      }

      if (!worst || returnValue < worst["Return"]) {
        worst = stock;
      }
    });

    setBestPerformer(best);
    setWorstPerformer(worst);
  };

  // Helper function to format the date to "Year"
  const formatYear = (date) => {
    return new Date(date).getFullYear();
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#121212",
        minHeight: "100vh",
        color: "#fff",
      }}
    >
      <div style={{ maxWidth: "800px", margin: "0 auto" }}>
        <h1
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            textAlign: "center",
            marginBottom: "20px",
            color: "#fff",
          }}
        >
          Victory.ai Fund
        </h1>

        {/* Best and Worst Performer */}
        <div
          style={{
            marginTop: "20px",
            padding: "20px",
            background: "#1c1c1c",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: "600",
              marginBottom: "15px",
              color: "#fff",
            }}
          >
            Best and Worst Performers
          </h2>
          {bestPerformer && worstPerformer && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <h3 style={{ color: "#28a745" }}>Best Performer</h3>
                <p>{bestPerformer.Stock}</p>
                <p>Return: {bestPerformer["Return"]}%</p>
              </div>
              <div>
                <h3 style={{ color: "#dc3545" }}>Worst Performer</h3>
                <p>{worstPerformer.Stock}</p>
                <p>Return: {worstPerformer["Return"]}%</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Securities to Add (Note Section) */}
        <div
          style={{
            marginTop: "20px",
            padding: "20px",
            background: "#1c1c1c",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: "600",
              marginBottom: "15px",
              color: "#fff",
            }}
          >
            Securities that can be Added to the portfolio
          </h2>
          {securitiesToAdd.length > 0 ? (
            <ul style={{ color: "#fff" }}>
              {securitiesToAdd.map((security, index) => (
                <li key={index}>{security}</li>
              ))}
            </ul>
          ) : (
            <p style={{ color: "#ccc" }}>No securities added yet.</p>
          )}
        </div>

        {/* Monthly Returns Bar Chart */}
        <div
          style={{
            marginTop: "40px",
            padding: "20px",
            background: "#1c1c1c",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: "600",
              marginBottom: "15px",
              color: "#fff",
            }}
          >
            Monthly Returns for Last 12 Months
          </h2>
          <BarChart
            width={500}
            height={300}
            data={monthlyReturns}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              content={({ payload }) => {
                if (payload && payload.length) {
                  const { name, return: returnValue } = payload[0].payload;
                  return (
                    <div
                      style={{
                        backgroundColor: "#1c1c1c",
                        padding: "10px",
                        borderRadius: "5px",
                        color: "#fff",
                      }}
                    >
                      <p><strong>{name}</strong></p>
                      <p>Return: {returnValue.toFixed(2)}%</p>
                    </div>
                  );
                }
                return null;
              }}
            />

            <Legend />
            <Bar dataKey="return" fill="#8884d8" />
          </BarChart>
        </div>

        {/* Portfolio Overview */}
        <div
          style={{
            marginTop: "30px",
            padding: "20px",
            background: "#1c1c1c",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h2
            style={{
              fontSize: "1.25rem",
              fontWeight: "600",
              marginBottom: "15px",
              color: "#fff",
            }}
          >
            Portfolio Overview (Performance Updates Monthly)
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Stock
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Purchase Date
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Buy Price (₹)
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Sell/Current Price (₹)
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Sell Date
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "10px",
                    borderBottom: "1px solid #ccc",
                  }}
                >
                  Return (%)
                </th>
              </tr>
            </thead>
            <tbody>
              {portfolio.map((stock, index) => {
                const currentDate = new Date();
                const sellDate = new Date(stock["Sell Date"]);
                const purchaseDate = new Date(stock["Purchase Date"]);

                // Format the dates to show only the year
                const purchaseYear = formatYear(purchaseDate);
                const sellYear = formatYear(sellDate);

                const isHolding = currentDate <= sellDate ? "HOLDING" : "SOLD"; // Corrected logic

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #444" }}>
                    <td style={{ padding: "10px", color: "#fff" }}>{stock.Stock}</td>
                    <td style={{ padding: "10px", color: "#fff" }}>{purchaseYear}</td> {/* Displaying only the year */}
                    <td style={{ padding: "10px", color: "#fff" }}>
                      {parseFloat(stock["Buy Price"]).toFixed(1)}
                    </td>
                    <td style={{ padding: "10px", color: "#fff" }}>
                      {parseFloat(stock["Sell Price"]).toFixed(1)}
                    </td>
                    <td style={{ padding: "10px", color: "#fff" }}>
                      {isHolding === "HOLDING" ? "-" : sellYear} {/* Displaying only the year */}
                    </td>
                    <td style={{ padding: "10px", color: "#fff" }}>{isHolding}</td>
                    <td style={{ padding: "10px", color: "#fff" }}>
                      {stock["Return"]}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FundPerformance;
